import styles from "./stepper.module.css";
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { friendlyError } from "../../../../shared/utils/apiErrors";
import StepIdentificacion from "./Steps/StepIdentificacion";
import StepContacto      from "./Steps/StepContacto";
import StepDireccion     from "./Steps/StepDireccion";
import StepConfirmacion  from "./Steps/StepConfirmacion";

// STEPS se construye dentro del componente usando t() para los títulos

const INITIAL_FORM = {
  cedula: "", tipo_cedula: "FISICA", nombre: "", email: "",
  dir_provincia: "", dir_canton: "", dir_distrito: "", dir_senas: "",
  contacto: "", observaciones: "", activo: true, telefonos: [],
};

function validateStep(step, formData, t) {
  const errs = {};
  if (step === 0) {
    if (!formData.nombre.trim()) errs.nombre = t("proveedores.nombreRequerido");
  }
  return errs;
}

export default function ProveedorStepper({ initial, proveedorId }) {
  const { t }       = useTranslation();
  const navigate    = useNavigate();
  const isEdit      = Boolean(proveedorId);

  const STEPS = [
    { numStep: 0, title: t("proveedores.stepIdentificacion"), component: StepIdentificacion },
    { numStep: 1, title: t("proveedores.stepContacto"),        component: StepContacto },
    { numStep: 2, title: t("proveedores.stepDireccion"),       component: StepDireccion },
    { numStep: 3, title: t("proveedores.stepConfirmacion"),    component: StepConfirmacion },
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [formData,   setFormData]   = useState(initial || INITIAL_FORM);
  const [errors,     setErrors]     = useState({});
  const [isLoading,  setLoading]    = useState(false);
  const progressRef = useRef(null);

  useEffect(() => {
    if (!progressRef.current) return;
    const width = activeStep / (STEPS.length - 1);
    progressRef.current.style.transform = `scaleX(${width})`;
  }, [activeStep, STEPS.length]);

  function handleNext(e) {
    e.preventDefault();
    const errs = validateStep(activeStep, formData, t);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setTimeout(() => {
        const el = document.querySelector("[data-has-error='true']");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setErrors({});
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function handlePrev(e) {
    e.preventDefault();
    setErrors({});
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isLoading) return;

    // Validación final
    const errs = validateStep(0, formData, t);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setActiveStep(0);
      return;
    }
    setErrors({});

    try {
      setLoading(true);
      const url    = isEdit ? `/api/proveedores/proveedor/${proveedorId}/` : "/api/proveedores/proveedor/";
      const method = isEdit ? "PUT" : "POST";
      const r = await apiFetch(url, { method, body: JSON.stringify(formData) });
      if (!r.ok) {
        const msg = await friendlyError(r, isEdit ? t("proveedores.errorActualizar") : t("proveedores.errorGuardar"));
        setErrors({ _global: msg });
        return;
      }
      navigate("/proveedores");
    } catch (err) {
      setErrors({ _global: err.message || t("proveedores.errorConexion") });
    } finally {
      setLoading(false);
    }
  }

  const hasErrors = Object.keys(errors).length > 0 && !errors._global;

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/"><span className={styles.inactive}>{t("common.inicio")} </span>/</Link>
        <Link to="/proveedores"><span className={styles.inactive}> {t("sidebar.proveedores")} </span>/</Link>
        <span className={styles.active}> {isEdit ? t("common.editar") : t("common.agregar")}</span>
      </span>

      <div className={styles.stepper} style={{ marginTop: 10 }}>
        <form className={styles["form-wizard"]}>
          <h1 className="title">{isEdit ? t("proveedores.editarTitulo") : t("proveedores.nuevoTitulo")}</h1>

          {/* Barra de progreso */}
          <div className={styles["progress-container"]}>
            <div ref={progressRef} className={styles.progress} />
            <ol>
              {STEPS.map((step) => (
                <li
                  key={step.title}
                  className={
                    activeStep === step.numStep ? styles.active
                    : activeStep > step.numStep ? styles.done
                    : ""
                  }
                >
                  <span>{step.title}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Banner errores de campo */}
          {hasErrors && (
            <div style={{
              background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)",
              borderLeft: "4px solid #ef4444", borderRadius: 8,
              padding: "10px 16px", margin: "0 0 12px",
              color: "#f87171", fontSize: "0.83rem",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <i className="fa-solid fa-triangle-exclamation" />
              {t("proveedores.erroresCampos")}
            </div>
          )}

          {/* Paso actual */}
          <div className={styles["steps-container"]}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35 }}
                style={{ width: "100%" }}
              >
                {(() => {
                  const Component = STEPS[activeStep].component;
                  return (
                    <Component
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                    />
                  );
                })()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controles */}
          <div className={styles.controls}>
            <button
              type="button"
              className={styles.btn}
              onClick={handlePrev}
              disabled={activeStep === 0}
            >
              {t("common.atras")}
            </button>

            {activeStep < STEPS.length - 1 ? (
              <button
                type="button"
                className={styles.btn}
                onClick={handleNext}
                style={{
                  background: hasErrors ? "rgba(239,68,68,0.15)" : undefined,
                  borderColor: hasErrors ? "rgba(239,68,68,0.5)" : undefined,
                }}
              >
                {hasErrors ? (
                  <><i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5 }} />{t("proveedores.corregirErrores")}</>
                ) : `${t("common.siguiente")} →`}
              </button>
            ) : (
              <button
                type="submit"
                className={styles.btn}
                disabled={isLoading}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />{t("common.guardando")}</>
                ) : isEdit ? t("proveedores.actualizarProveedor") : t("proveedores.guardarProveedor")}
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
}
